import { useState, useMemo, type ReactNode } from "react";
import { X, Check, Search, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/modern/components/ui/dialog-shadcn";
import { cn } from "@/modern/utils";
import { MarketingTag } from "@/bryntum-lib/models/MarketingTag";
import {
  FILE_DETAIL_FIELD_LABEL_INLINE_CLS,
} from "../../fileDetailFormClasses";

interface MarketingTagSelectorProps {
  tags: MarketingTag[];
  selectedTagIds: number[];
  onToggle: (tagId: number) => void;
  /** AI-suggested tag IDs – always displayed even when deselected. */
  suggestedTagIds?: number[];
  /** Renders on the same row as the section title (e.g. step help). */
  headerEnd?: ReactNode;
  /** Shows required asterisk on the section label. */
  required?: boolean;
  /** Validation message shown under the browse control. */
  error?: string;
}

export const MarketingTagSelector = ({
  tags,
  selectedTagIds,
  onToggle,
  suggestedTagIds,
  headerEnd,
  required,
  error,
}: MarketingTagSelectorProps) => {
  const [browseOpen, setBrowseOpen] = useState(false);

  const suggestedSet = useMemo(() => new Set(suggestedTagIds ?? []), [suggestedTagIds]);
  const suggestedTags = tags.filter((t) => suggestedSet.has(t.id));
  const nonSuggestedSelected = tags.filter((t) => selectedTagIds.includes(t.id) && !suggestedSet.has(t.id));

  const quickPickTags = useMemo(
    () => [...tags].sort((a, b) => b.id - a.id).slice(0, 15),
    [tags],
  );

  const visibleTags = useMemo(() => {
    const seen = new Set<number>();
    const result: MarketingTag[] = [];
    for (const t of suggestedTags) {
      seen.add(t.id);
      result.push(t);
    }
    for (const t of quickPickTags) {
      if (!seen.has(t.id)) {
        seen.add(t.id);
        result.push(t);
      }
    }
    for (const t of nonSuggestedSelected) {
      if (!seen.has(t.id)) {
        result.push(t);
      }
    }
    return result;
  }, [suggestedTags, quickPickTags, nonSuggestedSelected]);

  return (
    <div>
      <div className="mb-1.5 flex min-h-7 items-center justify-between gap-2">
        <label className={FILE_DETAIL_FIELD_LABEL_INLINE_CLS}>
          Solution tags
          {required ? <span className="text-destructive"> *</span> : null}
        </label>
        {headerEnd != null ? <div className="flex shrink-0 items-center">{headerEnd}</div> : null}
      </div>

      {visibleTags.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {visibleTags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id);
            const isSuggested = suggestedSet.has(tag.id);

            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => onToggle(tag.id)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-sm font-normal transition-colors",
                  isSelected
                    ? "border-amber-500/25 bg-amber-500/15 text-amber-700 dark:text-amber-400"
                    : "border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {isSelected && <Check className="size-2.5" />}
                {tag.label}
                {isSuggested && (
                  <span className="ml-0.5 inline-flex items-center gap-0.5 rounded px-1 py-px text-[10px] font-normal text-muted-foreground">
                    <Sparkles className="size-2" />
                    AI
                  </span>
                )}
                {isSelected && !isSuggested && (
                  <span className="ml-0.5 hover:text-destructive transition-colors">
                    <X className="w-3 h-3" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Browse button */}
      <button
        type="button"
        onClick={() => setBrowseOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-primary border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/60 transition-colors",
          error && "border-destructive/60 text-destructive bg-destructive/5",
        )}
      >
        <Search className="w-3 h-3" />
        Browse All Solution Tags
      </button>
      {error ? <p className="text-sm text-destructive mt-2">{error}</p> : null}

      <SolutionTagBrowseDialog
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        tags={tags}
        selectedTagIds={selectedTagIds}
        onToggle={onToggle}
        suggestedSet={suggestedSet}
      />
    </div>
  );
};

const SolutionTagBrowseDialog = ({
  open,
  onOpenChange,
  tags,
  selectedTagIds,
  onToggle,
  suggestedSet,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: MarketingTag[];
  selectedTagIds: number[];
  onToggle: (tagId: number) => void;
  suggestedSet: Set<number>;
}) => {
  const [search, setSearch] = useState("");

  const filteredTags = useMemo(() => {
    if (!search.trim()) return tags;
    const q = search.toLowerCase();
    return tags.filter((t) => t.label.toLowerCase().includes(q));
  }, [tags, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="z-[calc(var(--swivu-bryntum-z-index)+20)]"
        className="sm:max-w-lg p-0 gap-0 rounded-xl z-[calc(var(--swivu-bryntum-z-index)+20)]"
      >
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
          <h3 className="text-base font-semibold text-foreground">Solution Tags</h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            title="Close"
            className="h-7 w-7 rounded-full flex items-center justify-center border border-border hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <div className="px-5 pb-5 max-h-[50vh] overflow-y-auto">
          <div className="flex flex-wrap gap-1.5">
            {filteredTags.map((tag) => {
              const isSelected = selectedTagIds.includes(tag.id);
              const isSuggested = suggestedSet.has(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => onToggle(tag.id)}
                  className={cn(
                    "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border transition-all",
                    isSelected
                      ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40"
                      : "bg-transparent text-foreground border-border hover:bg-muted hover:border-muted-foreground/40",
                  )}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                  {tag.label}
                  {isSuggested && (
                    <span className="inline-flex items-center gap-0.5 ml-0.5 px-1 py-px rounded text-[10px] font-semibold bg-violet-500/15 text-violet-400">
                      <Sparkles className="w-2 h-2" />
                      AI
                    </span>
                  )}
                </button>
              );
            })}
            {filteredTags.length === 0 && tags.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center w-full">
                No solution tags are available. Activate a completed Context to generate marketing tags.
              </p>
            )}
            {filteredTags.length === 0 && tags.length > 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center w-full">
                No tags match your search.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
